import { Box, Button, Spinner, Text } from 'grommet';
import { FieldItem } from './FieldItem';
import { getPaddedBBox } from '../../service/common/utils';
import * as turf from '@turf/turf';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { UnenrolledFieldItem } from './UnenrolledFieldItem';
import { useEffect, useState } from 'react';
import { FormDown, FormUp } from 'grommet-icons';
import eyeIcon from '../../../../assets/BoundaryTool/eye-icon.svg';
import noEyeIcon from '../../../../assets/BoundaryTool/no-eye-icon.svg';
import { useDispatch } from 'react-redux';
import { focusField, hideFields, updateFieldInfo } from '../../../../redux/actions/map-actions/field/field-action';
import { Feature } from '@turf/helpers';
import { fitBoundsOption } from '../../../../redux/actions/map-actions/field/field-helper';
import { useAppSelector } from '../../../../redux';
import { getForagePlantSpecies } from '../../../../redux/actions/data-collection-action';
import { TrainingRegionList } from '../classification/TrainingRegionList';

//Todo: this scrollbar is not like the layout. Make it a ScrollBar component
export const FieldStep = ({ t, setFieldToDelete }: any) => {
  const dispatch = useDispatch();
  const { upsellingBoundaryTool } = useFlags();
  //todo: this scrollbar is not like the layout. Make it a ScrollBar component

  const [openEnrolled, setOpenEnrolled] = useState<boolean>(true);
  const [openUnenrolled, setOpenUnenrolled] = useState<boolean>(false);
  const [fieldsHidden, setFieldsHidden] = useState<boolean>(false);

  const country = useAppSelector((state) => state.producer.producerInformation?.country ?? 'us');
  const producerData = useAppSelector((state) => state.producer.producerInformation);
  const fields = useAppSelector((state) => state.map.fields);
  const map = useAppSelector((state) => state.map.map);
  const syncErrors = useAppSelector((state) => state.map.syncErrors);
  const practicesOnContract = useAppSelector((state) => state.map.practicesOnContract);
  const uploadingBoundaries = useAppSelector((state) => state.map.uploadingBoundaries);

  const region = useAppSelector((state) => state.user.userData.country);

  useEffect(() => {
    dispatch(getForagePlantSpecies(true));
  }, []);

  const fieldsSorted = fields.reduce(
    (res: any, f) => {
      if (f.properties?.unenrolledAt) {
        res.unenrolled.push(f);
        return res;
      }
      res.enrolled.push(f);
      return res;
    },
    { enrolled: [], unenrolled: [] },
  );

  const getFieldCollapsibles = (setFieldToDelete: (field: Feature) => void) => {
    return (
      <Box overflow={'auto'}>
        <Box direction={'column'} pad={'12px 0'} style={{ minHeight: 'unset', borderBottom: 'solid 1px #ccc', padding: '1rem' }}>
          <Box
            width={'100%'}
            direction={'row'}
            justify={'between'}
            align={'center'}
            gap={'16px'}
            height={'30px'}
            onClick={() => setOpenEnrolled(!openEnrolled)}>
            <Text data-cy={`enroll-collapsible-title`} textAlign={'start'} size={'20px'} style={{ fontWeight: 900, color: '#282828' }}>
              {t('fieldFlow.mainWizard.Fields.enrollCollapsibleTitle')} ({fieldsSorted.enrolled.length})
            </Text>
            <Box direction={'row'} align={'center'}>
              {openEnrolled ? <FormUp height={'12px'} width={'12px'} /> : <FormDown height={'12px'} width={'12px'} />}
            </Box>
          </Box>

          {openEnrolled && (
            <Box>
              <Box gap={'4px'}>
                {fieldsSorted.enrolled.map((field) => (
                  <FieldItem
                    key={field.id}
                    t={t}
                    field={field}
                    focusField={(field: any) => {
                      dispatch(focusField(field.id));
                      const newCenter = getPaddedBBox(turf.bbox(field.properties?.processedIneligibleBoundaries?.original.boundaries ?? field));
                      map?.fitBounds([newCenter[0], newCenter[1], newCenter[2], newCenter[3]], fitBoundsOption);
                    }}
                    saveField={(field) => {
                      dispatch(updateFieldInfo(field));
                    }}
                    deleteField={(field) => {
                      setFieldToDelete(field);
                    }}
                    syncErrors={syncErrors}
                    userRegion={region}
                    country={country}
                    upsellingBoundaryTool={upsellingBoundaryTool}
                    hidden={fieldsHidden}
                    producerData={producerData}
                    practicesOnContract={practicesOnContract ?? []}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {region !== 'BR' && (
          <Box direction={'column'} pad={'12px 0'} margin={'0 0 0 0'} style={{ minHeight: 'unset', borderBottom: 'solid 1px #ccc', padding: '1rem' }}>
            <Box direction={'row'} gap={'8px'}>
              {fieldsHidden ? (
                <Button
                  data-cy={`show-field-button`}
                  onClick={() => {
                    setFieldsHidden(false);
                    dispatch(hideFields(false));
                  }}>
                  <img
                    src={noEyeIcon}
                    height={24}
                    width={24}
                    alt='Show Fields Icon'
                    style={{
                      cursor: 'pointer',
                      alignSelf: 'center',
                    }}
                  />
                </Button>
              ) : (
                <Button
                  data-cy={`hide-field-button`}
                  onClick={() => {
                    setFieldsHidden(true);
                    dispatch(hideFields(true));
                  }}>
                  <img
                    src={eyeIcon}
                    height={24}
                    width={24}
                    alt='Hide Fields Icon'
                    style={{
                      cursor: 'pointer',
                      alignSelf: 'center',
                    }}
                  />
                </Button>
              )}

              <Box
                width={'100%'}
                direction={'row'}
                justify={'between'}
                align={'center'}
                gap={'16px'}
                height={'30px'}
                onClick={() => setOpenUnenrolled(!openUnenrolled)}>
                <Text data-cy={`unenroll-collapsible-title`} textAlign={'start'} size={'20px'} style={{ fontWeight: 900, color: '#282828' }}>
                  {t('fieldFlow.mainWizard.Fields.unenrollCollapsibleTitle')} ({fieldsSorted.unenrolled.length})
                </Text>
                <Box direction={'row'} align={'center'}>
                  {openUnenrolled ? <FormUp height={'12px'} width={'12px'} /> : <FormDown height={'12px'} width={'12px'} />}
                </Box>
              </Box>
            </Box>

            {openUnenrolled && (
              <Box>
                <Box gap={'16px'}>
                  {fieldsSorted.unenrolled.map((field) => (
                    <UnenrolledFieldItem
                      key={field.id}
                      t={t}
                      field={field}
                      focusField={(field: any) => {
                        focusField(field);
                        const newCenter = getPaddedBBox(turf.bbox(field.properties?.processedIneligibleBoundaries?.original.boundaries ?? field));
                        map?.fitBounds([newCenter[0], newCenter[1], newCenter[2], newCenter[3]], fitBoundsOption);
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
        <TrainingRegionList />
      </Box>
    );
  };

  return (
    <Box style={{ border: 'solid 1px #ccc', marginBottom: '1rem', marginTop: '1rem', borderRadius: '5px' }} height={'100%'}>
      {uploadingBoundaries && (
        <Box height={'100%'} align={'center'} justify={'center'}>
          <Spinner color={'#FFE137'} size={'medium'} />
        </Box>
      )}
      {!uploadingBoundaries && fields.length === 0 ? (
        <Text data-cy={'no-fields-text'} size={'16px'} color={'#232735'} textAlign={'center'} style={{ lineHeight: '24px' }}>
          {t('fieldFlow.mainWizard.Fields.notesPlaceholder')}
        </Text>
      ) : (
        <></>
      )}

      {(!!fieldsSorted.unenrolled.length || !!fieldsSorted.enrolled.length) && getFieldCollapsibles(setFieldToDelete)}
    </Box>
  );
};
